'use client';

// Third Party
import * as React from 'react';

// Utils
import { useReviews } from './reviews-context';

// UI
import { BaseModal } from '@components/base-modal';
import { ButtonIcon } from '@components/button-icon';
import { IconNames } from '@components/icon';
import { IndividualReview } from './individual-review';
import { ReviewStats } from './review-stats';
import { SavvyReviewSummary } from './savvy-review-summary';
import { Spinner, SpinnerSize } from '@components/spinner';
import { Button } from '@components/button';
import { ReviewsSearchSort } from './review-search-sort';
import { NoResultsFound } from './reviews-section';
import { Typography } from '@components/typography';

type Props = {
  reviewsAverageRating: number;
  reviewsCount: number;
  reviewSummary?: string;
};

// Custom hook to track scroll position
const useScrollTracker = (
  ref: React.RefObject<HTMLDivElement | null>,
  isActive: boolean,
  dependencies: unknown[]
) => {
  const [isScrolled, setIsScrolled] = React.useState(false);

  React.useEffect(() => {
    if (!isActive) {
      setIsScrolled(false);
      return;
    }

    let cleanup: (() => void) | undefined;

    const setupScrollListener = () => {
      const scrollContainer = ref.current;
      if (!scrollContainer) {
        return;
      }

      const handleScroll = () => {
        setIsScrolled(scrollContainer.scrollTop > 0);
      };

      scrollContainer.addEventListener('scroll', handleScroll, {
        passive: true,
      });
      handleScroll();

      cleanup = () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
      };
    };

    // Try immediate setup
    setupScrollListener();

    // If ref not ready, try again after a short delay
    if (!cleanup) {
      const timeoutId = setTimeout(setupScrollListener, 100);
      return () => {
        clearTimeout(timeoutId);
        cleanup?.();
      };
    }

    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, ...dependencies]);

  return isScrolled;
};

export const ReviewsModal: React.FC<Props> = ({
  reviewsAverageRating,
  reviewsCount,
  reviewSummary,
}) => {
  const {
    deactivateReviewsModal,
    reviewsModalActive,
    allReviews,
    loadMoreReviews,
    hasMoreReviews,
    reviewsLoading,
    searchValue,
    setSearchValue,
    sortValue,
    setSortValue,
    listingReviewsData,
  } = useReviews();

  // Get total count of reviews matching the search
  const totalSearchResults =
    listingReviewsData?.listingReviewsListAllForListing?.__typename ===
    'ListingReviewCollection'
      ? listingReviewsData.listingReviewsListAllForListing.meta.totalEntries ||
        0
      : 0;

  const showSearchResultsMessage =
    searchValue.trim() && allReviews.length > 0 && !reviewsLoading;

  const desktopScrollContainerRef = React.useRef<HTMLDivElement>(null);
  const mobileScrollContainerRef = React.useRef<HTMLDivElement>(null);

  const isDesktopScrolled = useScrollTracker(
    desktopScrollContainerRef,
    reviewsModalActive,
    [allReviews]
  );
  const isMobileScrolled = useScrollTracker(
    mobileScrollContainerRef,
    reviewsModalActive,
    [allReviews]
  );

  return (
    <BaseModal
      isOpen={reviewsModalActive}
      onClose={deactivateReviewsModal}
      zIndexLevel="fullScreenModal"
      backdropClassName="bg-gray-main/75"
      showCloseButton={false}
      size="full"
      ariaLabel="Reviews"
      className="fixed top-24 bottom-0 md:relative md:top-auto md:bottom-auto max-w-7xl md:h-5/6 rounded-t-lg md:rounded-lg"
    >
      <div className="flex flex-col h-full text-primary-main p-2 sm:p-4 sm:pb-0">
        {/* Modal Header */}
        <div className="flex items-center justify-between w-full px-4 pt-2 md:px-10 md:pt-4">
          <Typography
            component="h4"
            variant="title4"
            variantM="title4"
            className="text-primary-main"
          >
            Reviews
          </Typography>
          <ButtonIcon
            variant="text"
            color="gray"
            size="lg"
            isCircle={true}
            icon={IconNames.X}
            ariaLabel="Close"
            ariaDescribedby="close-reviews-modal"
            onClick={deactivateReviewsModal}
          />
        </div>

        {/* Desktop Layout */}
        <div className="hidden md:flex md:flex-1 md:flex-col md:overflow-hidden">
          <div className="flex items-start space-x-4 p-4 md:space-x-12 md:px-10 md:pb-8">
            <div className="flex-shrink-0">
              <ReviewStats
                averageRating={reviewsAverageRating}
                reviewsCount={reviewsCount}
              />
            </div>
            {reviewSummary && (
              <div className="flex-1">
                <SavvyReviewSummary reviewSummary={reviewSummary} />
              </div>
            )}
          </div>
          <div
            ref={desktopScrollContainerRef}
            className="flex-1 overflow-y-auto p-4 md:px-10 pt-0 space-y-6"
          >
            {/* Search and Sort Section */}
            <div
              className={`sticky top-0 z-10 pb-4 -mx-4 md:-mx-10 px-4 md:px-10 bg-white ${
                isDesktopScrolled ? 'border-b border-gray-light' : ''
              }`}
            >
              <ReviewsSearchSort
                searchValue={searchValue}
                sortValue={sortValue}
                onSearchChange={setSearchValue}
                onSortChange={(option) => setSortValue(option)}
              />
            </div>
            {/* Search Results Message */}
            {showSearchResultsMessage && (
              <p className="text-md text-gray-main">
                <span className="font-medium">{totalSearchResults}</span>{' '}
                {totalSearchResults === 1
                  ? 'review mentions'
                  : 'reviews mention'}{' '}
                &quot;
                <span className="font-medium">{searchValue}</span>
                &quot;
              </p>
            )}
            {/* Scrollable Individual Reviews */}
            {allReviews.length === 0 && reviewsLoading ? (
              <div className="flex justify-center items-start pt-2">
                <Spinner color="gray" size={SpinnerSize.SIZE_LARGE} />
              </div>
            ) : allReviews.length === 0 && searchValue.trim() ? (
              <NoResultsFound onClearSearch={() => setSearchValue('')} />
            ) : (
              <div className="space-y-6">
                {allReviews.map((review, index) => (
                  <IndividualReview
                    key={`${review.id || 'review'}-${index}`}
                    review={review}
                  />
                ))}
                {!reviewsLoading && hasMoreReviews && !searchValue.trim() && (
                  <div className="flex justify-center items-center py-4">
                    <Button
                      onClick={loadMoreReviews}
                      color="gray"
                      size="sm"
                      variant="outlined"
                      isFullWidth={false}
                      isLoading={reviewsLoading}
                      className="items-center justify-center"
                    >
                      Load More
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Mobile Layout: Stacked */}
        <div
          ref={mobileScrollContainerRef}
          className="md:hidden flex-1 overflow-y-auto px-4 md:p-6 lg:px-10 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
        >
          {/* Review Summary for Mobile */}
          <div className="mb-2 flex">
            <ReviewStats
              averageRating={reviewsAverageRating}
              reviewsCount={reviewsCount}
              className="justify-start my-2"
            />
          </div>

          {/* AI Summary Box for Mobile */}
          {reviewSummary && (
            <div className="mb-4 pt-4">
              <SavvyReviewSummary reviewSummary={reviewSummary} />
            </div>
          )}

          {/* Search and Sort Section - Sticky on Mobile */}
          <div
            className={`sticky -top-2 -mt-4 z-10 bg-white p-4 -mx-4 md:-mx-10 md:px-10 ${
              isMobileScrolled ? 'border-b border-gray-light' : ''
            }`}
          >
            <div className="flex justify-between items-center bg-white pt-4" />
            <ReviewsSearchSort
              searchValue={searchValue}
              sortValue={sortValue}
              onSearchChange={setSearchValue}
              onSortChange={(option) => setSortValue(option)}
            />
          </div>

          <div className="flex-1 overflow-y-auto px-4 md:p-6 space-y-4">
            {/* Search Results Message */}
            {showSearchResultsMessage && (
              <div>
                <p className="text-sm text-gray-main">
                  <span className="font-medium">{totalSearchResults}</span>{' '}
                  {totalSearchResults === 1
                    ? 'review mentions'
                    : 'reviews mention'}{' '}
                  &quot;
                  <span className="font-medium">{searchValue}</span>
                  &quot;
                </p>
              </div>
            )}
            {allReviews.length === 0 && reviewsLoading ? (
              <div className="flex justify-center items-center pt-4">
                <Spinner color="gray" size={SpinnerSize.SIZE_LARGE} />
              </div>
            ) : allReviews.length === 0 && searchValue.trim() ? (
              <div className="flex flex-col items-start justify-start py-12 bg-white">
                <NoResultsFound onClearSearch={() => setSearchValue('')} />
              </div>
            ) : (
              <div className="space-y-6 py-4">
                {allReviews?.map((review, index) => (
                  <IndividualReview
                    key={`${review.id || 'review'}-${index}`}
                    review={review}
                  />
                ))}

                {!reviewsLoading && hasMoreReviews && (
                  <div className="flex justify-center items-center py-4">
                    <Button
                      onClick={loadMoreReviews}
                      color="gray"
                      size="sm"
                      variant="outlined"
                      isLoading={reviewsLoading}
                      className="items-center justify-center"
                    >
                      Load More
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </BaseModal>
  );
};
